import React, { useCallback, useEffect, useRef, useState } from 'react';

// Spotlight tour. Steps name a CSS selector; the element is scrolled into
// view, cut out of the dimmed overlay, and described by a popover beside it.
// A step whose target isn't on the page (panel hidden by role, empty period,
// sidebar collapsed on mobile) falls back to a centred card rather than
// aborting — content can then describe optional panels safely.

const PAD = 8;          // breathing room around the highlighted element
const CARD_W = 340;
const GAP = 12;         // between highlight and popover

const visibleRect = (selector) => {
  const el = selector && document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  // Off-screen (e.g. the sidebar translated away on small screens)
  if (r.right <= 0 || r.bottom <= 0 || r.left >= window.innerWidth || r.top >= window.innerHeight) return null;
  return r;
};

export default function Tour({ steps: allSteps, label, onClose }) {
  // `optional` steps describe chrome that some pages simply don't render (the
  // filter bar is absent on Daily Digest, Subscribers and Admin). Drop those
  // once, at start, so the step count stays honest — everything else falls
  // back to a centred card if its target hasn't appeared yet.
  const [steps] = useState(() =>
    allSteps.filter(s => !s.optional || document.querySelector(s.target)));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(180);
  const cardRef = useRef(null);
  const scrolled = useRef(-1);

  const step = steps[i];
  const last = i === steps.length - 1;

  const next = useCallback(() => (last ? onClose() : setI(n => n + 1)), [last, onClose]);
  const back = useCallback(() => setI(n => Math.max(0, n - 1)), []);

  // Scroll the target into view once per step, then keep re-measuring.
  useEffect(() => {
    if (scrolled.current !== i) {
      scrolled.current = i;
      const el = step?.target && document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        const off = r.top < 80 || r.bottom > window.innerHeight - 200;
        if (off) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
    // Re-measure on a timer rather than on scroll: smooth scrolling, chart
    // animations and lazily-loaded panels all move the target without firing
    // anything useful. Only publish real movement — a fresh DOMRect every tick
    // would re-render the overlay several times a second for nothing.
    const same = (a, b) => (!a && !b) || (a && b
      && Math.abs(a.left - b.left) < 1 && Math.abs(a.top - b.top) < 1
      && Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1);
    const measure = () => {
      // altTarget covers chrome that moves out of reach on small screens —
      // e.g. the sidebar collapses behind the ☰ button below 900px.
      const found = visibleRect(step?.target) || visibleRect(step?.altTarget);
      setRect(prev => (same(prev, found) ? prev : found));
      const h = cardRef.current?.offsetHeight;
      if (h) setCardH(prev => (Math.abs(prev - h) < 2 ? prev : h));
    };
    measure();
    const timer = setInterval(measure, 150);
    window.addEventListener('resize', measure);
    return () => { clearInterval(timer); window.removeEventListener('resize', measure); };
  }, [i, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  if (!step) return null;

  // Popover placement: below the highlight, else above, else beside it (tall
  // targets like the sidebar), else centred when there's nothing to point at.
  const vw = window.innerWidth, vh = window.innerHeight;
  const width = Math.min(CARD_W, vw - 24);
  const clamp = (v, max) => Math.min(Math.max(12, v), Math.max(12, max));
  let cardStyle;
  if (rect) {
    const centreX = clamp(rect.left + rect.width / 2 - width / 2, vw - width - 12);
    const centreY = clamp(rect.top + rect.height / 2 - cardH / 2, vh - cardH - 12);
    if (rect.bottom + PAD + GAP + cardH <= vh - 12) {
      cardStyle = { width, left: centreX, top: rect.bottom + PAD + GAP };
    } else if (rect.top - PAD - GAP - cardH >= 12) {
      cardStyle = { width, left: centreX, top: rect.top - PAD - GAP - cardH };
    } else if (rect.right + PAD + GAP + width <= vw - 12) {
      cardStyle = { width, left: rect.right + PAD + GAP, top: centreY };
    } else if (rect.left - PAD - GAP - width >= 12) {
      cardStyle = { width, left: rect.left - PAD - GAP - width, top: centreY };
    } else {
      cardStyle = { width, left: centreX, top: clamp(vh - cardH - 16, vh - cardH - 12) };
    }
  } else {
    cardStyle = { width, left: (vw - width) / 2, top: Math.max(60, vh / 2 - cardH / 2) };
  }

  return (
    <div className="tour">
      {/* The spotlight paints its own dim via a huge box-shadow; without a
          target the scrim has to supply it. */}
      <div className={`tour-scrim ${rect ? '' : 'dim'}`} onClick={onClose} />
      {rect && (
        <div
          className="tour-spot"
          style={{
            left: rect.left - PAD,
            top: rect.top - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}
      <div ref={cardRef} className="tour-card" style={cardStyle} role="dialog" aria-modal="true" aria-label={step.title}>
        <div className="tour-head">
          <span className="tour-step">{label} · {i + 1} of {steps.length}</span>
          <button className="tour-x" onClick={onClose} aria-label="Close tour">×</button>
        </div>
        <h4>{step.title}</h4>
        <p>{step.body}</p>
        <div className="tour-foot">
          <div className="tour-dots">
            {steps.map((s, n) => (
              <button
                key={n}
                className={`tour-dot ${n === i ? 'on' : ''}`}
                onClick={() => setI(n)}
                aria-label={`Step ${n + 1}: ${s.title}`}
              />
            ))}
          </div>
          <div className="tour-btns">
            {i > 0 && <button className="btn-ghost" onClick={back}>Back</button>}
            <button className="btn-primary inline" onClick={next}>{last ? 'Done' : 'Next'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
